import type { FC, ReactElement, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ListItem, UnorderedList, VStack } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { useCustomerValidateTrackOrder } from '../../../../bff-api/customers/useCustomerValidateTrackOrder';
import { BasicError } from '../../../../components/basic-error/BasicError';
import { Field } from '../../../../components/field';
import { redirectWithPost } from '../../../../components/utils';
import { Button, Header, Hyperlink, Text } from '../../../../ui-components';
import { useUrl } from '../../../../utils/api';
import { getDeployTarget } from '../../../../utils/url-utils';
import { PageUrl } from '../../../utils/page-url';
import { useTrackOrderFields } from './useTrackOrderFields';
import type { CustomersValidateTrackOrderRequest } from '../../../../bff-api/customers/model';

const orderNoPattern: Record<string, MessageDescriptor> = defineMessages({
    production: {
        defaultMessage: 'Starts with "SF" followed by a 10-digit code',
        id: 'account.track_order.order_number.hint.production',
    },
    staging: {
        defaultMessage: 'Starts with "SFT" followed by a 10-digit code',
        id: 'account.track_order.order_number.hint.staging',
    },
    development: {
        defaultMessage: 'Starts with "SL-DEV" followed by a 10-digit code',
        id: 'account.track_order.order_number.hint.development',
    },
    default: {
        defaultMessage: 'It is a 8-digit code',
        id: 'account.track_order.order_number.hint.default',
    },
});

const contactUs = (contactUsUrl: string) =>
    function HardLink(chunks: ReactNode[]): ReactElement {
        return (
            <Hyperlink
                href={contactUsUrl}
                type="hard"
                showLoader
                data-testid="account-track-order-contact-us"
            >
                {chunks}
            </Hyperlink>
        );
    };

export const TrackOrder: FC = () => {
    const form = useForm<CustomersValidateTrackOrderRequest>();
    const fields = useTrackOrderFields(form);
    const mutation = useCustomerValidateTrackOrder();
    const loginShowUrl = useUrl(PageUrl.LOGIN_FORM);
    const [isError, setIsError] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const hint = useMemo(
        () => orderNoPattern[getDeployTarget() ?? ''] ?? orderNoPattern.default,
        [],
    );
    const contactUsUrl = useUrl(PageUrl.CUSTOMER_CONTACT_US);

    const handleSubmit = useCallback(
        (data: CustomersValidateTrackOrderRequest): void => {
            mutation.mutate(data, {
                onSuccess: (isValid) => {
                    if (isValid) {
                        setIsRedirecting(true);
                        redirectWithPost(loginShowUrl, {
                            dwfrm_ordertrack_orderNumber: data.orderNo,
                            dwfrm_ordertrack_orderEmail: data.email,
                            dwfrm_ordertrack_findorder: 'Check+Status',
                        });
                        return;
                    }
                    setIsError(true);
                },
                onError: () => {
                    setIsError(true);
                },
            });
        },
        [mutation, loginShowUrl],
    );

    return (
        <VStack
            spacing={6}
            width="100%"
            align="stretch"
            maxWidth={{ base: '100%', sm: '448px', xl: '640px' }}
            margin={{ base: '1rem 1rem', md: '2rem', lg: '2.5rem', xl: '4rem' }}
        >
            <Header as="h3" variant="header3" color="slt-pepper.1000">
                <FormattedMessage id="account.track_order.title" defaultMessage="Track Order" />
            </Header>

            {form.formState.isSubmitted && isError ? (
                <BasicError
                    options={{
                        alertDataTestId: 'account-track-order-error-container',
                        errorMessageDataTestId: 'account-track-order-error-message',
                        showOutline: false,
                        iconVerticalAlign: 'top',
                    }}
                >
                    <FormattedMessage
                        id="account.track_order.error.invalid"
                        defaultMessage="Order not found. Please check your details and try again."
                    />
                </BasicError>
            ) : null}

            <form
                data-testid="account-track-order-form"
                onSubmit={form.handleSubmit(handleSubmit)}
                noValidate
            >
                <VStack spacing={6} align="stretch">
                    <VStack spacing={2} align="stretch">
                        <Field {...fields.orderNo} />
                        <Text>
                            <FormattedMessage {...hint} />
                        </Text>
                    </VStack>

                    <Field {...fields.email} />

                    <Button
                        kind="submit"
                        variant="primary-black"
                        className="w-full uppercase"
                        wide
                        isLoading={mutation.isLoading || isRedirecting}
                        isDisabled={mutation.isLoading}
                    >
                        <FormattedMessage
                            id="account.track_order.button"
                            defaultMessage="View Order"
                        />
                    </Button>
                </VStack>
            </form>

            <VStack spacing={1} align="stretch">
                <Text style="bold">
                    <FormattedMessage
                        id="account.track_order.help.title"
                        defaultMessage="Can't find a purchase?"
                    />
                </Text>
                <Text>
                    <FormattedMessage
                        id="account.track_order.help.description.1"
                        defaultMessage="The form above cannot retrieve:"
                    />
                </Text>
                <UnorderedList>
                    <ListItem
                        paddingInlineStart={2}
                        sx={{
                            '::marker': {
                                fontSize: '10px',
                            },
                        }}
                    >
                        <FormattedMessage
                            id="account.track_order.help.description.2"
                            defaultMessage="In-store purchases"
                        />
                    </ListItem>
                </UnorderedList>
                <Text>
                    <FormattedMessage
                        id="account.track_order.help.description.3"
                        defaultMessage="If you need help finding one of these orders, <contactUs>contact us</contactUs>"
                        values={{
                            contactUs: contactUs(contactUsUrl),
                        }}
                    />
                </Text>
            </VStack>
        </VStack>
    );
};
